import { createContext, useCallback, useEffect, useState } from 'react'
import { alTocarElPopup, avisoDePrueba, type PreferenciasDeAviso } from './avisos'
import {
  avisar, desbloquearSonido, guardarPopup, guardarSonido, haySonido, iconoDelAviso,
  pedirPopup, permisoDelNavegador, prepararAudio, prepararPopup, quierePopup,
  quiereSonido, sonar,
} from './campana'

/**
 * Las preferencias de aviso y lo que se puede hacer con ellas: el estado del
 * botón «Avisos» de la cabecera y de la invitación de la lista.
 *
 * Lo usa el Layout, que es dueño del estado, y lo publica por `AvisosCtx` para
 * que la invitación de la lista —que vive dentro de la bandeja y de En vivo—
 * active lo mismo que el botón y abra el MISMO panel: así la persona ve dónde
 * queda para la próxima vez.
 *
 * `estado` arranca en `null` hasta leer las preferencias: sin eso la
 * invitación aparecería un instante a quien ya tiene todo activo.
 */
export function useAvisos() {
  const [estado, setEstado] = useState<PreferenciasDeAviso | null>(null)
  const [panelAbierto, setPanelAbierto] = useState(false)
  const [pidiendo, setPidiendo] = useState(false)

  // Se relee TODO cada vez, del almacenamiento y del navegador: el permiso
  // puede cambiar fuera del portal —el candado de la barra de direcciones— y
  // el audio despierta con un gesto que no pasa por acá.
  const publicar = useCallback(() => {
    setEstado({
      sonido: quiereSonido(),
      popup: quierePopup(),
      audioListo: haySonido(),
      permiso: permisoDelNavegador(),
    })
  }, [])

  useEffect(() => {
    publicar()
    void prepararPopup()
    const quitarAudio = prepararAudio(publicar)
    const alVolver = () => { if (document.visibilityState === 'visible') publicar() }
    document.addEventListener('visibilitychange', alVolver)
    window.addEventListener('focus', publicar)
    // Chrome avisa cuando el permiso cambia desde el candado; sin esto el
    // interruptor seguiría diciendo «bloqueado» hasta volver a la pestaña.
    let permiso: PermissionStatus | null = null
    navigator.permissions?.query({ name: 'notifications' as PermissionName })
      .then((s) => { permiso = s; s.onchange = publicar })
      .catch(() => { /* sin Permissions API: alcanza con el foco */ })
    return () => {
      quitarAudio()
      document.removeEventListener('visibilitychange', alVolver)
      window.removeEventListener('focus', publicar)
      if (permiso) permiso.onchange = null
    }
  }, [publicar])

  /** Al encenderlo suena una vez: confirma que se oye y, de paso, es el gesto
   *  que el navegador exige para despertar el audio. */
  const cambiarSonido = useCallback(async () => {
    const ahora = !quiereSonido()
    guardarSonido(ahora)
    if (ahora && await desbloquearSonido()) sonar()
    publicar()
  }, [publicar])

  const cambiarPopup = useCallback(async () => {
    const toque = alTocarElPopup({ popup: quierePopup(), permiso: permisoDelNavegador() })
    if (!toque) return
    if (toque.pedirPermiso) {
      setPidiendo(true)
      try {
        await pedirPopup()
      } finally {
        setPidiendo(false)
      }
    }
    guardarPopup(toque.encender)
    publicar()
  }, [publicar])

  /**
   * Lo que hace «Activar» en la invitación: abre el panel PRIMERO —para que la
   * persona vea dónde queda esto y los interruptores encendiéndose— y prende
   * las dos cosas en el mismo clic, que es cuando el cuadro de permiso del
   * navegador menos se bloquea.
   */
  const activarTodo = useCallback(async () => {
    setPanelAbierto(true)
    guardarSonido(true)
    guardarPopup(true)
    const pedir = permisoDelNavegador() === 'pedible'
    if (pedir) setPidiendo(true)
    try {
      await Promise.all([desbloquearSonido(), pedir ? pedirPopup() : Promise.resolve(false)])
    } finally {
      if (pedir) setPidiendo(false)
    }
    publicar()
  }, [publicar])

  /** Probar es lo que convierte «está activado» en «lo escuché». Suena una vez
   *  sin tocar ninguna preferencia: es una comprobación, no un cambio. */
  const probarSonido = useCallback(async () => {
    await desbloquearSonido()
    sonar()
    publicar()
  }, [publicar])

  /** Sale por el mismo camino que un aviso real —service worker, botón y
   *  todo—: una prueba que se viera distinta no probaría nada. */
  const probarPopup = useCallback(() => {
    const { titulo, opciones } = avisoDePrueba(iconoDelAviso())
    avisar(titulo, opciones)
  }, [])

  return {
    estado, panelAbierto, setPanelAbierto, pidiendo,
    cambiarSonido, cambiarPopup, activarTodo, probarSonido, probarPopup,
  }
}

export type ControlDeAvisos = ReturnType<typeof useAvisos>

/**
 * Lo que la invitación de la lista y el parlante del chat necesitan del
 * Layout. Fuera del Layout
 * —una pantalla montada sola— vale `null` y la invitación no se muestra.
 */
export const AvisosCtx = createContext<
  Pick<ControlDeAvisos, 'estado' | 'activarTodo' | 'setPanelAbierto' | 'cambiarSonido'> | null
>(null)
